import React, { useEffect, useState } from "react";
import {
  Box,
  Flex,
  Avatar,
  Button,
  Link as ChakraLink,
  IconButton,
  useDisclosure,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useBreakpointValue,
  VStack,
  Skeleton,
  HStack,
} from "@chakra-ui/react";
import {
  Link as ReactRouterLink,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { HamburgerIcon } from "@chakra-ui/icons";

const links = [
  { href: "/home", label: "Home" },
  { href: "/about", label: "About" },
  { href: "/services", label: "Services" },
  { href: "/experience", label: "Experience" },
  { href: "/portfolio", label: "Portfolio" },
  { href: "/contact", label: "Contact" },
];

const linkStyles = {
  fontSize: "md",
  color: "#fff",
  fontWeight: "bold",
  padding: "5px 10px",
  _hover: {
    textDecoration: "none",
  },
  _active: {
    background: "#fff",
    color: "#000",
    borderRadius: "5px",
  },
};

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [titleDocument, setTitleDocument] = useState("Sharon Nyambura");
  const [activeLink, setActiveLink] = useState(location.pathname);
  const [loading, setLoading] = useState(true);

  const currentYear = new Date().getFullYear();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const isMobile = useBreakpointValue({ base: true, md: false });

  const handleClick = (to) => {
    navigate(to);
    setTitleDocument(to);
    setTimeout(() => {
      const section = document.getElementById(to.substring(1));

      const yOffset = -80; // Adjust this value based on the height of your header
      const y = section.getBoundingClientRect().top + window.scrollY + yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
    }, 100); // Add a slight delay to ensure the route has changed
  };

  // effect to change document title
  useEffect(() => {
    document.title =
      titleDocument === "Sharon Nyambura"
        ? "Sharon Nyambura | UI/UX Designer"
        : `Sharon Nyambura | ${titleDocument
            .charAt(1)
            .toUpperCase()}${titleDocument.slice(2)}`;
  }, [titleDocument]);

  // effect to change active link state on scroll
  useEffect(() => {
    const handleScroll = () => {
      const yOffset = 100; // Offset to trigger active link
      const sections = links.map((link) =>
        document.getElementById(link.href.substring(1))
      );
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (section && section.getBoundingClientRect().top < yOffset) {
          setActiveLink(links[i].href);
          break;
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Simulate a data fetch and remove the loading state
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 2000); // Adjust the timeout as needed
    return () => clearTimeout(timer);
  }, []);

  const renderLinks = () =>
    links.map((link) => {
      const to = link.href;
      // const isActive = location.pathname === to;
      return (
        <ChakraLink
          sx={{
            ...linkStyles,
            ...(activeLink === link.href ? linkStyles._active : {}),
          }}
          as="span"
          key={link.href}
          mx={2}
          fontWeight="bold"
          _hover={{ textDecoration: "none" }}
          onClick={() => {
            handleClick(to);
            if (isMobile) {
              onClose(); // Close menu on link click for mobile
            }
          }}
        >
          {link.label}
        </ChakraLink>
      );
    });

  return (
    <Box
      boxShadow="md"
      p={5}
      position="fixed"
      width="100%"
      top={0}
      bg="#530054"
      zIndex={1}
    >
      <Flex alignItems="center" justifyContent="space-between">
        <Skeleton isLoaded={!loading} borderRadius="full">
          <ChakraLink to="/" as={ReactRouterLink}>
            <Avatar src="sn.jpg" name="Sharon Nyambura" />
          </ChakraLink>
        </Skeleton>

        {!isMobile && (
          <>
            {loading
              ? Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton
                    key={i}
                    height="40px"
                    width="100px"
                    borderRadius="10px"
                  />
                ))
              : renderLinks()}
            <Skeleton isLoaded={!loading}>
              <Button
                ml={4}
                onClick={() => {
                  handleClick("/contact");
                }}
              >
                Let's Talk
              </Button>
            </Skeleton>
          </>
        )}

        <Skeleton isLoaded={!loading}>
          {isMobile && (
            <IconButton
              icon={<HamburgerIcon />}
              aria-label="Toggle Navigation"
              onClick={onOpen}
            />
          )}
        </Skeleton>
      </Flex>

      <Drawer isOpen={isOpen} placement="left" onClose={onClose}>
        <DrawerOverlay>
          <DrawerContent bg="#530054" size="full">
            <DrawerCloseButton />
            <DrawerHeader textAlign="center">Menu</DrawerHeader>
            <DrawerBody>
              <VStack>{renderLinks()}</VStack>
            </DrawerBody>
            <DrawerFooter>@ {currentYear} Sharon Nyambura</DrawerFooter>
          </DrawerContent>
        </DrawerOverlay>
      </Drawer>
    </Box>
  );
};

export default Header;
